import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { checkIsAdminLoggined } from '../../../../utils/utils.js';
import { bankAccountsApi } from '../../../../api/bankApi';
import {
  bankTypeLabelUa,
  getBankAccountsApiError,
  hasBankAccountInvoiceRequisites,
  normalizeIban,
  resolveBankType,
  validateInvoiceRequisitesForm,
} from '../../../../utils/bankAccountInvoiceUtils';
import { Helmet } from 'react-helmet';
import Header from '../../../Header';
import Footer from '../../../Footer';
import InvoiceRequisitesModal from './InvoiceRequisitesModal';
import privatbankIcon from '../../../../assets/images/privatbank.png';
import monobankIcon from '../../../../assets/images/monobank.png';

const BankTypeBadge = ({ account }) => {
  const isMonobank = resolveBankType(account) === 'MONOBANK';
  return (
    <span className={`badge badge-${isMonobank ? 'primary' : 'success'} d-inline-flex align-items-center`}>
      <img
        src={isMonobank ? monobankIcon : privatbankIcon}
        alt={isMonobank ? 'Monobank' : 'PrivatBank'}
        style={{ width: '16px', height: '16px', marginRight: '4px', verticalAlign: 'middle' }}
      />
      {isMonobank ? 'Монобанк' : 'ПриватБанк'}
    </span>
  );
};

const AdminBankAccountsPage = props => {
  const token = localStorage.getItem('token');
  const history = useHistory();

  const [accounts, setAccounts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [syncPeriodAccount, setSyncPeriodAccount] = useState(null);
  const [syncFromDate, setSyncFromDate] = useState('');
  const [syncToDate, setSyncToDate] = useState('');
  const [editingAccount, setEditingAccount] = useState(null);
  const [requisitesAccount, setRequisitesAccount] = useState(null);
  const [requisitesSaving, setRequisitesSaving] = useState(false);
  const [requisitesError, setRequisitesError] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Form fields
  const [name, setName] = useState('');
  const [bankType, setBankType] = useState('MONOBANK');
  const [apiToken, setApiToken] = useState('');
  const [hasToken, setHasToken] = useState(false); // Чи встановлений токен
  const [accountId, setAccountId] = useState('');
  const [merchantId, setMerchantId] = useState('');
  const [iban, setIban] = useState(''); // Privat integration IBAN → accountId
  const [recipientName, setRecipientName] = useState('');
  const [edrpou, setEdrpou] = useState('');
  const [invoiceIban, setInvoiceIban] = useState('');
  const [bankName, setBankName] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [showBankTypeDropdown, setShowBankTypeDropdown] = useState(false);

  const resetInvoiceRequisiteFields = () => {
    setRecipientName('');
    setEdrpou('');
    setInvoiceIban('');
    setBankName('');
  };

  const showPageAlert = (type, message, timeoutMs = 3000) => {
    if (type === 'success') {
      setSuccess(message);
      setError('');
      setTimeout(() => setSuccess(''), timeoutMs);
    } else {
      setError(message);
      setSuccess('');
      setTimeout(() => setError(''), timeoutMs || 5000);
    }
  };

  useEffect(() => {
    checkIsAdminLoggined(props);
    window.scrollTo(0, 0);
    loadAccounts();
  }, []);

  // Закриваємо dropdown при кліку поза ним
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showBankTypeDropdown && !event.target.closest('.position-relative')) {
        setShowBankTypeDropdown(false);
      }
    };

    if (showBankTypeDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showBankTypeDropdown]);

  const loadAccounts = () => {
    setIsLoading(true);
    bankAccountsApi.getAll()
      .then(res => {
        if (res.status === 200 && res.data.data) {
          setAccounts(res.data.data);
        }
        setIsLoading(false);
      })
      .catch(error => {
        console.error('Error loading bank accounts:', error);
        setIsLoading(false);
      });
  };

  const handleAdd = () => {
    setEditingAccount(null);
    setName('');
    setBankType('MONOBANK');
    setApiToken('');
    setHasToken(false);
    setAccountId('');
    setMerchantId('');
    setIban('');
    setIsActive(true);
    setError('');
    setSuccess('');
    setShowBankTypeDropdown(false);
    // Очищаємо повідомлення на загальній сторінці при відкритті модалки
    setTimeout(() => {
      setError('');
      setSuccess('');
    }, 100);
  };

  const handleEdit = (account) => {
    const accountBankType = resolveBankType(account);
    setEditingAccount(account);
    setName(account.name || '');
    setBankType(accountBankType);
    setApiToken('');
    setHasToken(true);
    setIsActive(account.isActive !== false);

    if (accountBankType === 'MONOBANK') {
      setAccountId(account.accountId || '');
      setIban('');
      setMerchantId('');
    } else {
      setIban(account.accountId || '');
      setMerchantId(account.merchantId || '');
      setAccountId('');
    }

    setError('');
    setSuccess('');
    setShowBankTypeDropdown(false);
  };

  const handleOpenRequisites = (account) => {
    const accountBankType = resolveBankType(account);
    setRequisitesAccount(account);
    setRecipientName(account.recipientName || '');
    setEdrpou(account.edrpou || '');
    const fallbackIban = accountBankType === 'PRIVATBANK' ? (account.accountId || '') : '';
    setInvoiceIban(account.iban || fallbackIban);
    setBankName(account.bankName || '');
    setRequisitesError('');
    setSuccess('');
  };

  const closeRequisitesModal = () => {
    setRequisitesAccount(null);
    setRequisitesSaving(false);
    resetInvoiceRequisiteFields();
    setRequisitesError('');
  };

  const handleSubmitRequisites = () => {
    if (!requisitesAccount || requisitesSaving) return;
    const validationError = validateInvoiceRequisitesForm({
      recipientName,
      edrpou,
      iban: invoiceIban,
      bankName,
    });
    if (validationError) {
      setRequisitesError(validationError);
      return;
    }
    setRequisitesError('');
    setRequisitesSaving(true);
    bankAccountsApi.update(requisitesAccount.id, {
      recipientName: recipientName.trim(),
      edrpou: edrpou.trim(),
      iban: normalizeIban(invoiceIban),
      bankName: bankName.trim(),
    })
      .then((res) => {
        if (res.status === 200) {
          loadAccounts();
          if (window.$) {
            window.$('#invoiceRequisitesModal').modal('hide');
          }
          closeRequisitesModal();
          showPageAlert('success', 'Реквізити збережено');
        }
      })
      .catch((err) => {
        setRequisitesError(getBankAccountsApiError(err, 'Помилка при збереженні реквізитів'));
      })
      .finally(() => setRequisitesSaving(false));
  };

  const handleDelete = (account) => {
    if (window.confirm(`Ви дійсно хочете видалити рахунок "${account.name}"?`)) {
      bankAccountsApi.delete(account.id)
        .then(res => {
          if (res.status === 200) {
            showPageAlert('success', 'Рахунок успішно видалено');
            loadAccounts();
          }
        })
        .catch(err => {
          showPageAlert('error', getBankAccountsApiError(err, 'Помилка при видаленні рахунку'), 5000);
        });
    }
  };

  const handleDeactivate = (account) => {
    bankAccountsApi.deactivate(account.id)
      .then(res => {
        if (res.status === 200) {
          showPageAlert('success', 'Рахунок успішно деактивовано');
          loadAccounts();
        }
      })
      .catch(err => {
        showPageAlert('error', getBankAccountsApiError(err, 'Помилка при деактивації рахунку'), 5000);
      });
  };

  const handleSync = (account) => {
    bankAccountsApi.sync(account.id)
      .then(res => {
        if (res.status === 200) {
          showPageAlert('success', 'Синхронізацію завершено', 5000);
          loadAccounts();
        }
      })
      .catch(err => {
        showPageAlert('error', getBankAccountsApiError(err, 'Помилка синхронізації'), 5000);
      });
  };

  const handleOpenSyncPeriod = (account) => {
    setSyncPeriodAccount(account);
    // Встановлюємо за замовчуванням: від - 7 днів назад, до - сьогодні
    const today = new Date();
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(today.getDate() - 7);

    // Форматуємо тільки дату (YYYY-MM-DD)
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    setSyncFromDate(formatDate(sevenDaysAgo));
    setSyncToDate(formatDate(today));
    setError('');
    setSuccess('');
  };

  const handleSyncForPeriod = () => {
    if (!syncFromDate || !syncToDate) {
      setError('Виберіть обидві дати');
      return;
    }

    const from = new Date(syncFromDate);
    const to = new Date(syncToDate);

    if (from > to) {
      setError('Дата початку не може бути пізніше дати закінчення');
      return;
    }

    // Встановлюємо час: від - на початок дня (00:00:00), до - на кінець дня (23:59:59)
    from.setHours(0, 0, 0, 0);
    to.setHours(23, 59, 59, 999);

    // Форматуємо дати для API (yyyy-MM-ddTHH:mm:ss)
    const fromFormatted = from.toISOString().slice(0, 19);
    const toFormatted = to.toISOString().slice(0, 19);

    bankAccountsApi.syncForPeriod(syncPeriodAccount.id, fromFormatted, toFormatted)
      .then(res => {
        if (res.status === 200) {
          setSuccess('Синхронізацію за період завершено успішно');
          loadAccounts();
        }
      })
      .catch(error => {
        const res = error.response;
        const errorMessage = res?.data?.data || res?.data?.errorMessage || 'Помилка синхронізації за період';
        setError(errorMessage);
      });
  };


  const handleSubmit = () => {
    // Валідація: при створенні токен обов'язковий, при редагуванні - тільки якщо не встановлений
    if (!name || !bankType) {
      setError('Заповніть обов\'язкові поля');
      return;
    }

    // При створенні токен обов'язковий
    if (!editingAccount && !apiToken) {
      setError('Заповніть обов\'язкові поля');
      return;
    }

    // При редагуванні токен обов'язковий тільки якщо він не встановлений
    if (editingAccount && !hasToken && !apiToken) {
      setError('Заповніть обов\'язкові поля');
      return;
    }

    if (bankType === 'MONOBANK' && !accountId) {
      setError('Для Монобанку потрібно вказати ID рахунку');
      return;
    }

    if (bankType === 'PRIVATBANK' && (!merchantId || !iban)) {
      setError('Для ПриватБанку потрібно вказати ID мерчанта та IBAN рахунок');
      return;
    }

    setError('');
    setSuccess('');

    const data = {
      name,
      bankType,
      token: apiToken,
      accountId: bankType === 'MONOBANK' ? accountId : iban, // Для PrivatBank IBAN в accountId
      merchantId: bankType === 'PRIVATBANK' ? merchantId : undefined, // Для PrivatBank merchantId окремо
    };

    if (editingAccount) {
      // Оновлення
      const updateData = { ...data };
      if (!apiToken) {
        // Якщо токен не введено, не оновлюємо його
        delete updateData.token;
      }
      updateData.isActive = isActive;

      // Приват: якщо invoice IBAN ще не заданий або збігався зі старим integration IBAN — синхронізуємо
      if (bankType === 'PRIVATBANK') {
        const prevIntegrationIban = normalizeIban(editingAccount.accountId);
        const prevInvoiceIban = normalizeIban(editingAccount.iban);
        const nextIntegrationIban = normalizeIban(iban);
        if (!prevInvoiceIban || prevInvoiceIban === prevIntegrationIban) {
          updateData.iban = nextIntegrationIban;
        }
      }

      bankAccountsApi.update(editingAccount.id, updateData)
        .then(res => {
          if (res.status === 200) {
            setSuccess('Рахунок успішно оновлено');
            loadAccounts();
          }
        })
        .catch(error => {
          const res = error.response;
          const errorMessage = res?.data?.data || res?.data?.errorMessage || 'Помилка при оновленні рахунку';
          setError(errorMessage);
        });
    } else {
      // Створення
      bankAccountsApi.create(data)
        .then(res => {
          if (res.status === 200) {
            setSuccess('Рахунок успішно створено');
            loadAccounts();
          }
        })
        .catch(error => {
          const res = error.response;
          const errorMessage = res?.data?.data || res?.data?.errorMessage || 'Помилка при створенні рахунку';
          setError(errorMessage);
        });
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    // Якщо дата вже відформатована з бекенду (String), просто повертаємо її
    if (typeof dateString === 'string' && dateString.includes(',')) {
      return dateString;
    }
    // Інакше форматуємо як раніше
    const date = new Date(dateString);
    return date.toLocaleString('uk-UA');
  };

  return (
    <>
      <div className='container'>
        <Helmet>
          <title>Адмін панель - Банківські рахунки</title>
        </Helmet>
        <div className='row'>
          <div className='col p-0'>
            <Header />
          </div>
        </div>

        <div className='row'>
          <div className='col p-0'>
            <div className='d-flex justify-content-center align-items-center mb-3 position-relative py-3'>
              <h2 className='mb-0 text-center'>Банківські рахунки</h2>
              <button
                className='btn btn-primary position-absolute'
                onClick={handleAdd}
                data-toggle='modal'
                data-target='#bankAccountModal'
                style={{ right: 0 }}
              >
                <i className='bi bi-plus-circle mr-2'></i>
                Додати рахунок
              </button>
            </div>

            {error && !requisitesAccount && !syncPeriodAccount && !editingAccount && (
              <div className='alert alert-danger alert-dismissible fade show mb-3' role='alert'>
                {error}
                <button type='button' className='close' onClick={() => setError('')} aria-label='Close'>
                  <span aria-hidden='true'>&times;</span>
                </button>
              </div>
            )}

            {success && !requisitesAccount && !syncPeriodAccount && (
              <div className='alert alert-success alert-dismissible fade show mb-3' role='alert'>
                {success}
                <button type='button' className='close' onClick={() => setSuccess('')} aria-label='Close'>
                  <span aria-hidden='true'>&times;</span>
                </button>
              </div>
            )}

            {isLoading ? (
              <div className='text-center py-5'>
                <div className='spinner-border' role='status'>
                  <span className='sr-only'>Завантаження...</span>
                </div>
              </div>
            ) : accounts.length === 0 ? (
              <div className='text-center py-5'>
                <p>Рахунків не знайдено</p>
              </div>
            ) : (
              <div className='table-responsive'>
                <table className='table table-striped table-hover'>
                  <thead>
                  <tr>
                    <th>ID</th>
                    <th>Назва</th>
                    <th>Банк</th>
                    <th>Активний</th>
                    <th>Реквізити</th>
                    <th>Остання синхронізація</th>
                    <th>Кількість оплат</th>
                    <th>Дії</th>
                  </tr>
                  </thead>
                  <tbody>
                  {accounts.map(account => (
                    <tr key={account.id}>
                      <td>{account.id}</td>
                      <td>{account.name}</td>
                      <td>
                        <BankTypeBadge account={account} />
                      </td>
                      <td>
                        {account.isActive ? (
                          <span className='badge badge-success'>Так</span>
                        ) : (
                          <span className='badge badge-secondary'>Ні</span>
                        )}
                      </td>
                      <td>
                        {hasBankAccountInvoiceRequisites(account) ? (
                          <span className='badge badge-success' title={[account.recipientName, account.edrpou, account.iban].filter(Boolean).join(' · ')}>
                            Так
                          </span>
                        ) : (
                          <span className='badge badge-warning'>Ні</span>
                        )}
                      </td>
                      <td>{formatDate(account.lastSyncDate)}</td>
                      <td>{account.paymentsCount || 0}</td>
                      <td>
                        <button
                          className='btn btn-outline-primary btn-sm mr-1'
                          onClick={() => handleEdit(account)}
                          data-toggle='modal'
                          data-target='#bankAccountModal'
                          title='Редагувати інтеграцію'
                        >
                          <i className='bi bi-pencil-fill text-dark'></i>
                        </button>
                        <button
                          className={`btn btn-sm mr-1 ${hasBankAccountInvoiceRequisites(account) ? 'btn-outline-success' : 'btn-outline-warning'}`}
                          onClick={() => handleOpenRequisites(account)}
                          data-toggle='modal'
                          data-target='#invoiceRequisitesModal'
                          title='Реквізити для рахунку на оплату'
                        >
                          <i className='bi bi-receipt'></i>
                        </button>
                        <button
                          className='btn btn-outline-info btn-sm mr-1'
                          onClick={() => handleSync(account)}
                          title='Синхронізувати'
                        >
                          <i className='bi bi-arrow-repeat'></i>
                        </button>
                        <button
                          className='btn btn-outline-secondary btn-sm mr-1'
                          onClick={() => handleOpenSyncPeriod(account)}
                          data-toggle='modal'
                          data-target='#syncPeriodModal'
                          title='Синхронізувати за період'
                        >
                          <i className='bi bi-calendar-range'></i>
                        </button>
                        {account.isActive && (
                          <button
                            className='btn btn-outline-warning btn-sm mr-1'
                            onClick={() => handleDeactivate(account)}
                            title='Деактивувати'
                          >
                            <i className='bi bi-pause-circle'></i>
                          </button>
                        )}
                        <button
                          className='btn btn-outline-danger btn-sm'
                          onClick={() => handleDelete(account)}
                        >
                          <i className='bi bi-trash'></i>
                        </button>
                      </td>
                    </tr>
                  ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal для додавання/редагування */}
      <div 
        className='modal fade'
        id='bankAccountModal' 
        tabIndex='-1' 
        role='dialog'
        aria-labelledby='bankAccountModalLabel'
        aria-hidden='true'
      >
        <div className='modal-dialog modal-lg modal-dialog-centered' role='document'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title font-weight-bold' id='bankAccountModalLabel'>
                {editingAccount ? 'Редагувати рахунок' : 'Додати рахунок'}
              </h5>
              <button 
                type='button' 
                className='close' 
                data-dismiss='modal'
                aria-label='Close'
                onClick={() => {
                  setEditingAccount(null);
                  setName('');
                  setMerchantId('');
                  setBankType('MONOBANK');
                  setShowBankTypeDropdown(false);
                  setError('');
                }}
              >
                <span aria-hidden='true'>&times;</span>
              </button>
            </div>
            <div className='modal-body'>
              <form>
                <div className='form-group'>
                  <label>Назва</label>
                  <input
                    type='text'
                    className='form-control'
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder='Наприклад: Mono ФОП'
                  />
                </div>

                <div className='form-group'>
                  <label>Інтегрувати з</label>
                  <div className='position-relative'>
                    <input
                      type='text'
                      className='form-control'
                      readOnly
                      value={bankType === 'MONOBANK' ? 'Monobank' : 'Privatbank Business'}
                      onClick={() => setShowBankTypeDropdown(!showBankTypeDropdown)}
                      style={{ cursor: 'pointer', backgroundColor: 'white' }}
                    />
                    {showBankTypeDropdown && (
                      <div
                        className='position-absolute w-100 bg-white border rounded shadow-lg'
                        style={{
                          zIndex: 1000,
                          top: '100%',
                          marginTop: '2px',
                        }}
                      >
                        <div
                          className='p-2'
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            const newBankType = 'MONOBANK';
                            setBankType(newBankType);
                            setShowBankTypeDropdown(false);

                            // Визначаємо попередній тип банку (якщо редагуємо)
                            let previousBankType = null;
                            if (editingAccount && editingAccount.bankType) {
                              const prevType = typeof editingAccount.bankType === 'object' && editingAccount.bankType !== null
                                ? (editingAccount.bankType.databaseValue || editingAccount.bankType.name)
                                : editingAccount.bankType;
                              previousBankType = prevType ? prevType.toUpperCase() : null;
                            }

                            // При зміні типу банку очищаємо поля іншого типу
                            if (previousBankType === 'PRIVATBANK') {
                              setAccountId('');
                            }
                            setIban('');
                            setMerchantId('');
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          <img 
                            src={monobankIcon} 
                            alt='Monobank' 
                            style={{ width: '20px', height: '20px', marginRight: '8px', verticalAlign: 'middle' }}
                          />
                          Monobank
                        </div>
                        <div
                          className='p-2 border-top'
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            const newBankType = 'PRIVATBANK';
                            setBankType(newBankType);
                            setShowBankTypeDropdown(false);

                            // Визначаємо попередній тип банку (якщо редагуємо)
                            let previousBankType = null;
                            if (editingAccount && editingAccount.bankType) {
                              const prevType = typeof editingAccount.bankType === 'object' && editingAccount.bankType !== null
                                ? (editingAccount.bankType.databaseValue || editingAccount.bankType.name)
                                : editingAccount.bankType;
                              previousBankType = prevType ? prevType.toUpperCase() : null;
                            }

                            // При зміні типу банку очищаємо поля іншого типу
                            if (previousBankType === 'MONOBANK') {
                              setIban('');
                              setMerchantId('');
                            }
                            setAccountId('');
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#f8f9fa'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'white'}
                        >
                          <img 
                            src={privatbankIcon} 
                            alt='PrivatBank' 
                            style={{ width: '20px', height: '20px', marginRight: '8px', verticalAlign: 'middle' }}
                          />
                          Privatbank Business
                        </div>
                      </div>
                    )}
                  </div>
                  {bankType === 'MONOBANK' && (
                    <small className='text-muted'>
                      <i className='bi bi-question-circle mr-1'></i>
                      <a href='https://api.monobank.ua/docs/' target='_blank' rel='noopener noreferrer'>
                        Інструкція по підключенню Monobank
                      </a>
                    </small>
                  )}
                  {bankType === 'PRIVATBANK' && (
                    <small className='text-muted'>
                      <i className='bi bi-question-circle mr-1'></i>
                      <a
                        href='https://docs.google.com/document/d/e/2PACX-1vTtKvGa3P4E-lDqLg3bHRF6Wi9S7GIjSMFEFxII5qQZBGxuTXs25hQNiUU1hMZQhOyx6BNvIZ1bVKSr/pub'
                        target='_blank' rel='noopener noreferrer'>
                        Інструкція по підключенню Privatbank Business
                      </a>
                    </small>
                  )}
                </div>

                <div className='form-group'>
                  <label>
                    {bankType === 'MONOBANK' ? 'Ключ API' : 'Токен'}
                  </label>
                  <input
                    type='text'
                    className='form-control'
                    value={apiToken}
                    onChange={(e) => {
                      setApiToken(e.target.value);
                      if (e.target.value) {
                        setHasToken(false); // Якщо користувач вводить новий токен, скидаємо прапорець
                      }
                    }}
                    placeholder={
                      editingAccount && hasToken && !apiToken
                        ? '•••••••• (токен встановлений, введіть новий для зміни)'
                        : bankType === 'MONOBANK' ? 'Введіть ключ API' : 'Введіть токен'
                    }
                  />
                  {editingAccount && hasToken && !apiToken && (
                    <small className='form-text text-muted'>
                      <i className='bi bi-check-circle text-success mr-1'></i>
                      Токен встановлений. Залиште поле порожнім, щоб не змінювати токен, або введіть новий токен для
                      заміни.
                    </small>
                  )}
                  {editingAccount && !hasToken && !apiToken && (
                    <small className='form-text text-muted'>
                      <i className='bi bi-exclamation-circle text-warning mr-1'></i>
                      Токен не встановлений. Введіть токен для активації рахунку.
                    </small>
                  )}
                </div>

                {bankType === 'MONOBANK' && (
                  <div className='form-group'>
                    <label>Рахунок</label>
                    <input
                      type='text'
                      className='form-control'
                      value={accountId}
                      onChange={(e) => setAccountId(e.target.value.replace(/\s/g, ''))}
                      placeholder='ID рахунку'
                    />
                    <small className='form-text text-muted'>
                      Введіть ID рахунку з Monobank
                    </small>
                  </div>
                )}

                {bankType === 'PRIVATBANK' && (
                  <>
                    <div className='form-group'>
                      <label>ID мерчанта</label>
                      <input
                        type='text'
                        className='form-control'
                        value={merchantId}
                        onChange={(e) => setMerchantId(e.target.value)}
                        placeholder='Введіть ID мерчанта'
                      />
                    </div>

                    <div className='form-group'>
                      <label>IBAN рахунок</label>
                      <input
                        type='text'
                        className='form-control'
                        value={iban}
                        onChange={(e) => setIban(e.target.value.replace(/\s/g, ''))}
                        placeholder='Введіть рахунок'
                      />
                    </div>
                  </>
                )}

                {editingAccount && (
                  <div className='form-group'>
                    <div className='form-check'>
                      <input
                        type='checkbox'
                        className='form-check-input'
                        id='isActiveCheck'
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                      />
                      <label className='form-check-label' htmlFor='isActiveCheck'>
                        Активний
                      </label>
                    </div>
                  </div>
                )}
              </form>

              {error && (
                <div className='alert alert-danger alert-dismissible fade show mt-3 mb-0' role='alert'>
                  {error}
                  <button type='button' className='close' onClick={() => setError('')} aria-label='Close'>
                    <span aria-hidden='true'>&times;</span>
                  </button>
                </div>
              )}
              {success && (
                <div className='alert alert-success alert-dismissible fade show mt-3 mb-0' role='alert'>
                  {success}
                  <button type='button' className='close' onClick={() => setSuccess('')} aria-label='Close'>
                    <span aria-hidden='true'>&times;</span>
                  </button>
                </div>
              )}
            </div>
            <div className='modal-footer'>
              <button 
                type='button' 
                className='btn btn-secondary' 
                data-dismiss='modal'
                onClick={() => {
                  setEditingAccount(null);
                  setName('');
                  setMerchantId('');
                  setBankType('MONOBANK');
                  setShowBankTypeDropdown(false);
                  setError('');
                }}
              >
                Закрити
              </button>
              <button type='button' className='btn btn-primary' onClick={handleSubmit}>
                {editingAccount ? 'Зберегти' : 'Додати'}
              </button>
            </div>
          </div>
        </div>
      </div>

      <InvoiceRequisitesModal
        account={requisitesAccount}
        recipientName={recipientName}
        edrpou={edrpou}
        invoiceIban={invoiceIban}
        bankName={bankName}
        onRecipientNameChange={setRecipientName}
        onEdrpouChange={setEdrpou}
        onInvoiceIbanChange={setInvoiceIban}
        onBankNameChange={setBankName}
        onClose={closeRequisitesModal}
        onSubmit={handleSubmitRequisites}
        saving={requisitesSaving}
        error={requisitesError}
        onClearError={() => setRequisitesError('')}
      />

      <div 
        className='modal fade'
        id='syncPeriodModal' 
        tabIndex='-1' 
        role='dialog'
        aria-labelledby='syncPeriodModalLabel'
        aria-hidden='true'
      >
        <div className='modal-dialog modal-md modal-dialog-centered' role='document'>
          <div className='modal-content'>
            <div className='modal-header'>
              <h5 className='modal-title font-weight-bold' id='syncPeriodModalLabel'>Синхронізація за період</h5>
              <button 
                type='button' 
                className='close' 
                data-dismiss='modal'
                aria-label='Close'
                onClick={() => {
                  setSyncPeriodAccount(null);
                  setSyncFromDate('');
                  setSyncToDate('');
                  setError('');
                  setSuccess('');
                }}
              >
                <span aria-hidden='true'>&times;</span>
              </button>
            </div>
            <div className='modal-body'>
              {syncPeriodAccount && (
                <div className='mb-3'>
                  <p><strong>Рахунок:</strong> {syncPeriodAccount.name}</p>
                  <p><strong>Банк:</strong> {bankTypeLabelUa(syncPeriodAccount)}</p>
                </div>
              )}

              <form>
                <div className='row'>
                  <div className='col-md-6'>
                    <div className='form-group'>
                      <label>Дата від:</label>
                      <input
                        type='date'
                        className='form-control'
                        value={syncFromDate}
                        onChange={(e) => setSyncFromDate(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                  <div className='col-md-6'>
                    <div className='form-group'>
                      <label>Дата до:</label>
                      <input
                        type='date'
                        className='form-control'
                        value={syncToDate}
                        onChange={(e) => setSyncToDate(e.target.value)}
                        required
                      />
                    </div>
                  </div>
                </div>
              </form>

              {error && (
                <div className='alert alert-danger alert-dismissible fade show mt-3 mb-0' role='alert'>
                  {error}
                  <button type='button' className='close' onClick={() => setError('')} aria-label='Close'>
                    <span aria-hidden='true'>&times;</span>
                  </button>
                </div>
              )}
              {success && (
                <div className='alert alert-success alert-dismissible fade show mt-3 mb-0' role='alert'>
                  {success}
                  <button type='button' className='close' onClick={() => setSuccess('')} aria-label='Close'>
                    <span aria-hidden='true'>&times;</span>
                  </button>
                </div>
              )}
            </div>
            <div className='modal-footer'>
              <button 
                type='button' 
                className='btn btn-secondary' 
                data-dismiss='modal'
                onClick={() => {
                  setSyncPeriodAccount(null);
                  setSyncFromDate('');
                  setSyncToDate('');
                  setError('');
                  setSuccess('');
                }}
              >
                Закрити
              </button>
              <button type='button' className='btn btn-primary' onClick={handleSyncForPeriod}>
                Синхронізувати
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AdminBankAccountsPage;
